<template>
    <div class="max-w-500 mx-auto min-h-screen bg-white" data-cy="container_financial_health_result">
        <!-- Header -->
        <div class="px-4 pt-4 pb-2">
            <img 
                :src="logoImage" 
                alt="Bangkok Bank" 
                class="h-8 w-auto object-contain"
            />
        </div>
        
        <!-- Overall Score Section -->
        <div class="px-4 py-8 pb-8">
            <!-- Multi-ring Graph with Score Label -->
            <div class="graph-container flex justify-center mb-4" ref="graphContainer">
                <!-- Score Label positioned at ring endpoint -->
                <ScoreLabel
                    :icon="selectedRingData.icon"
                    :label="selectedRingData.label"
                    :score="selectedRingData.score"
                    :maxScore="100"
                    :position="scoreLabelPosition"
                />
                
                <CircularGraph
                    ref="circularGraph"
                    :rings="overallRings"
                    :score="totalScore"
                    :maxScore="100"
                    :size="260"
                    :strokeWidth="14"
                    :gap="7"
                    strokeLinecap="butt"
                    @ring-click="handleRingClick"
                />
            </div>
            
            <!-- Overall Title & Description -->
            <div class="text-left mb-2 px-4">
                <h1 class="text-24 font-bold text-a-black mb-4">
                    {{ overallContent.title }}
                </h1>
                <p class="text-16 text-a-black leading-relaxed">
                    {{ overallContent.description }}
                </p>
            </div>
        </div>
        
        <!-- Tabs Section -->
        <div class="px-4">
            <ResultTab
                data-cy="component_financial_health_result_tabs"
                :tabs="tabs"
                :activeTab="activeTab"
                @change="activeTab = $event"
            />
        </div>
        
        <!-- Tab Content -->
        <div class="px-4 py-4 pb-8">
            <div class="flex gap-4 flex-col">
                <div class="flex gap-4 justify-between px-4">
                    <!-- Left Content -->
                    <div class="flex-1 w-auto">
                        <!-- Category Icon -->
                        <img 
                            :src="currentTabData.icon" 
                            :alt="currentTabData.label"
                            class="w-8 h-8 mb-4"
                        />
                        
                        <!-- Title -->
                        <h3 class="text-14 font-bold mt-6" :style="{ color: currentTabData.titleColor }">
                            {{ currentTabData.content.title }}
                        </h3>
                        
                        <!-- Description -->
                        <p class="text-14 text-a-black leading-relaxed mb-4" :style="{ color: currentTabData.titleColor }">
                            {{ currentTabData.content.shortDescription }}
                        </p>
                    </div> 
                    
                    <!-- Right - Single Circle Graph -->
                    <div class="flex-shrink-0 w-[150px] mx-auto">
                        <SingleCircleGraph
                            :score="currentTabData.score"
                            :maxScore="100"
                            :size="150"
                            :strokeWidth="14"
                            :color="currentTabData.color"
                            :scoreFontSize="28"
                        />
                    </div>
                </div> 
                <div class="flex-1 px-4 border-t border-[rgba(0, 0, 0, 0.10)] pt-4">
                    <!-- Main Title -->
                    <h4 class="text-16 font-bold text-a-black">
                        {{ currentTabData.content.mainTitle }}
                    </h4>
                    
                    <!-- Main Description -->
                    <p class="text-16 text-a-black leading-relaxed">
                        {{ currentTabData.content.mainDescription }}
                    </p>
                </div>
            </div>
        </div>
        
        <!-- Bottom Button -->
        <div class="sticky bottom-0 bg-white px-8 pb-8 pt-4">
            <PrimaryButton 
                data-cy="btn_financial_health_view_plankton"
                :text="buttonText"
                @click="goToHistory"
            />
        </div>
    </div>
</template>

<script>
import CircularGraph from '@/components/Graph/CircularGraph.vue';
import SingleCircleGraph from '@/components/Graph/SingleCircleGraph.vue';
import ScoreLabel from '@/components/Graph/ScoreLabel.vue';
import ResultTab from '../component/ResultTab.vue';
import PrimaryButton from '@/components/Button/PrimaryButton.vue';
import BzbsFinancialHealth from '@/core/FinancialHealth/service/BzbsFinancialHealth.js';
import Locale from '@/helper/locale/Locale.js';

export default {
    name: "FinancialHealthResult",
    components: {
        CircularGraph,
        SingleCircleGraph,
        ScoreLabel,
        ResultTab,
        PrimaryButton
    },
    data() {
        return {
            logoImage: require('@/assets/icon/healthcheck/logo.webp'),
            activeTab: 'spend',
            isLoading: true,
            surveyResult: null,
            // Selected ring index (0 = Spend by default)
            selectedRingIndex: 0,
            // Graph configuration
            graphSize: 260,
            graphStrokeWidth: 14,
            graphGap: 7,
            // Category scores from API
            categoryScores: {
                spend: 0,
                save: 0,
                borrow: 0,
                plan: 0
            },
            // Ring icons (static)
            ringIcons: {
                spend: require('@/assets/icon/healthcheck/bbl_icons_32px_creditcard.webp'),
                save: require('@/assets/icon/healthcheck/savemore.webp'),
                borrow: require('@/assets/icon/healthcheck/bbl_icons_32px_loanfinance.webp'),
                plan: require('@/assets/icon/healthcheck/bbl_icons_32px_insurance.webp')
            },
            // Category colors (static)
            categoryColors: {
                spend: '#0064ff',
                save: '#051c3f',
                borrow: '#7DB0FF',
                plan: '#8EDDB2'
            },
            // Content based on score ranges for each category
            categoryContent: {
                spend: {
                    icon: require('@/assets/icon/healthcheck/bbl_icons_32px_creditcard.webp'),
                    color: '#0064FF',
                    //titleColor: '#0064ff', 
                },
                save: {
                    icon: require('@/assets/icon/healthcheck/savemore.webp'),
                    color: '#002850', 
                },
                borrow: {
                    icon: require('@/assets/icon/healthcheck/bbl_icons_32px_loanfinance.webp'),
                    color: '#7DB0FF', 
                },
                plan: {
                    icon: require('@/assets/icon/healthcheck/bbl_icons_32px_insurance.webp'),
                    color: '#8EDDB2', 
                }
            }, 
        };
    },
    computed: {
        // Get locale labels
        lbl() {
            return Locale.getLbl().financial_health;
        },
        // Dynamic tabs from locale
        tabs() {
            return [
                { id: 'spend', label: this.lbl.categories.spend, color: '#0064ff' },
                { id: 'save', label: this.lbl.categories.save, color: '#051c3f' },
                { id: 'borrow', label: this.lbl.categories.borrow, color: '#7DB0FF' },
                { id: 'plan', label: this.lbl.categories.plan, color: '#8EDDB2' }
            ];
        },
        // Dynamic ring labels from locale
        ringLabels() {
            return [
                { id: 'spend', label: this.lbl.categories.spend, icon: this.ringIcons.spend },
                { id: 'save', label: this.lbl.categories.save, icon: this.ringIcons.save },
                { id: 'borrow', label: this.lbl.categories.borrow, icon: this.ringIcons.borrow },
                { id: 'plan', label: this.lbl.categories.plan, icon: this.ringIcons.plan }
            ];
        },
        // Button text from locale
        buttonText() {
            return this.lbl.buttons.view_plankton;
        },
        totalScore() {
            const { spend, save, borrow, plan } = this.categoryScores;
            return Math.round((spend + save + borrow + plan) / 4);
        },
        overallRings() {
            return [
                { value: this.categoryScores.spend, color: '#0064FF', backgroundColor: '#F5F5F5' },
                { value: this.categoryScores.save, color: '#002850', backgroundColor: '#F5F5F5' },
                { value: this.categoryScores.borrow, color: '#7DB0FF', backgroundColor: '#F5F5F5' },
                { value: this.categoryScores.plan, color: '#8EDDB2', backgroundColor: '#F5F5F5' }
            ];
        },
        overallContent() {
            if (this.totalScore < 40) {
                return this.lbl.overall.low;
            } else if (this.totalScore < 80) {
                return this.lbl.overall.mid;
            }
            return this.lbl.overall.high;
        },
        currentTabData() {
            const score = this.categoryScores[this.activeTab];
            
            let rangeKey = 'low';
            if (score >= 80) {
                rangeKey = 'high';
            } else if (score >= 40) {
                rangeKey = 'mid';
            }
            
            // Get content from locale
            const categoryLocale = this.lbl[this.activeTab];
            
            return {
                icon: this.ringIcons[this.activeTab],
                color: this.categoryColors[this.activeTab],
                score: score,
                label: this.lbl.categories[this.activeTab],
                content: categoryLocale[rangeKey]
            };
        },
        // Get selected ring data (icon, label, score)
        selectedRingData() {
            const ringLabel = this.ringLabels[this.selectedRingIndex];
            const categoryId = ringLabel?.id || 'spend';
            return {
                icon: ringLabel?.icon || this.ringIcons.spend,
                label: ringLabel?.label || this.lbl.categories.spend,
                score: this.categoryScores[categoryId] || 0
            };
        },
        // Calculate position for ScoreLabel at the START of the ring arc
        scoreLabelPosition() {
            const index = this.selectedRingIndex;
            
            // Graph dimensions
            const size = this.graphSize;
            const strokeWidth = this.graphStrokeWidth;
            const gap = this.graphGap;
            
            // Calculate radius for this ring (same logic as CircularGraph)
            const maxRadius = (size / 2) - (strokeWidth / 2) - 4;
            const radius = maxRadius - (index * (strokeWidth + gap));
            
            // Arc starts at top (12 o'clock position)
            // Start position: x = center, y = center - radius
            const center = size / 2;
            const startY = center - radius;
            
            let isSmallScreen = window.innerWidth < 380;
            let offset = isSmallScreen ? 60 : 70;
            // Position label at center-top of arc, offset 20px to the right
            return {
                top: startY - 12, // Vertically centered on the ring start
                left: center + offset // 20px to the right of center (top of arc)
            };
        }
    },
    mounted() {
        // Fetch survey answers from API
        this.fetchSurveyResult();
    },
    watch: {
        activeTab(newTab) {
            // Update selected ring index to match active tab
            const tabOrder = ['spend', 'save', 'borrow', 'plan'];
            this.selectedRingIndex = tabOrder.indexOf(newTab);
        }
    },
    methods: {
        fetchSurveyResult() {
            this.isLoading = true;
            
            BzbsFinancialHealth.apiGetSurveyAnswer().subscribe({
                next: (result) => {
                    if (result) {
                        this.surveyResult = result;
                        // Set category scores from API response
                        this.categoryScores.spend = Math.round(result.spendCategoryScore || 0);
                        this.categoryScores.save = Math.round(result.saveCategoryScore || 0);
                        this.categoryScores.borrow = Math.round(result.borrowCategoryScore || 0);
                        this.categoryScores.plan = Math.round(result.planCategoryScore || 0);
                    }
                    this.isLoading = false;
                },
                error: (err) => {
                    console.error('Error fetching survey result:', err);
                    this.isLoading = false;
                    // If error, redirect back to main page
                    // this.$router.replace({ name: 'financial-health' });
                }
            });
        },
        goToHistory() {
            this.$router.push({ name: 'history-point' });
        },
        handleRingClick(data) {
            // Update selected ring index when a ring is clicked
            this.selectedRingIndex = data.index;
            // Also change active tab to match the clicked ring
            const tabOrder = ['spend', 'save', 'borrow', 'plan'];
            this.activeTab = tabOrder[data.index];
        }
    }
};
</script>

<style scoped lang="scss">
.graph-container {
    position: relative;
}
</style>